<script lang="ts">
  import { subscriptionStore } from '$lib/stores/subscription'
  import { apiConfigurationService } from '$lib/services/ApiConfigurationService'
  import { onMount } from 'svelte'
  import { useDebounce, useTimeout } from '$lib/hooks/useTimer'
  import UsageDisplay from './api/UsageDisplay.svelte';
  import GrammarProviderCard from './api/GrammarProviderCard.svelte';
  import type { ProviderType } from '$lib/services/providers/types';
  
  // Import provider icons  
  import bareunIcon from '../../assets/bareun-big.png'
  import naraSpellerIcon from '../../assets/nara-speller-icon.svg'
  
  // Grammar providers configuration
  interface GrammarProvider {
    id: ProviderType
    name: string
    icon: string
    description: string
    requiresServerUrl: boolean
    requiresApiKey: boolean
    defaultServerUrl?: string
    enabled: boolean
    apiKey?: string
    serverUrl?: string
    guideUrl?: string
  }
  
  let providers = $state<GrammarProvider[]>([
    {
      id: 'naraSpeller' as ProviderType,
      name: '부산대 맞춤법 검사기',
      icon: naraSpellerIcon,
      description: '부산대학교 한국어 맞춤법 검사 서비스',
      requiresServerUrl: false,
      requiresApiKey: true,
      defaultServerUrl: 'https://dcplxo2e85.execute-api.ap-northeast-2.amazonaws.com',
      enabled: false,
      apiKey: '',
      serverUrl: '',
      guideUrl: 'https://nara-speller.co.kr/speller'
    },
    {
      id: 'bareun' as ProviderType,
      name: 'Bareun.ai',
      icon: bareunIcon,
      description: '고성능 한국어 맞춤법 검사 서비스',
      requiresServerUrl: true,
      requiresApiKey: true,
      enabled: false,
      apiKey: '',
      serverUrl: '',
      guideUrl: 'https://bareun.ai/register'
    }
  ])
  
  // Subscription info
  let subscription = $state({
    type: 'free' as 'free' | 'trial' | 'premium',
    dailySpellCheckLimit: 10,
    currentDailyUsage: {
      spellChecks: 0,
      aiAnalyses: 0
    },
    monthlyUsage: 0
  })
  
  // Auto-save state
  let showSaveIndicator = $state(false)
  let showApiKeys = $state<Record<string, boolean>>({})
  
  // Timer for hiding save indicator
  const hideSaveIndicatorTimer = useTimeout(() => {
    showSaveIndicator = false
  }, 1000, false)
  
  
  // Debounced save function
  const debouncedSave = useDebounce(() => {
    // Find the enabled provider
    const enabledProvider = providers.find(p => p.enabled)
    
    if (enabledProvider) {
      // For NaraSpeller, use the default URL if no custom URL is provided
      const finalServerUrl = enabledProvider.id === 'naraSpeller' 
        ? (enabledProvider.defaultServerUrl || enabledProvider.serverUrl || '')
        : (enabledProvider.serverUrl || '');
        
      apiConfigurationService.saveConfiguration({
        useCustomServer: true,
        serverUrl: finalServerUrl,
        apiKey: enabledProvider.apiKey || '',
        providerType: enabledProvider.id,
        weakOpt: 0
      })
    } else {
      // No provider enabled
      apiConfigurationService.saveConfiguration({
        useCustomServer: false,
        serverUrl: '',
        apiKey: '',
        providerType: undefined,
        weakOpt: 0
      })
    }
    
    showSaveIndicator = true
    hideSaveIndicatorTimer.start()
  }, 500)
  
  // Initialize from settings
  onMount(() => {
    // Load custom server settings
    const config = apiConfigurationService.getCurrentConfiguration()
    
    // Update providers based on saved configuration
    if (config.useCustomServer) {
      providers = providers.map(p => {
        // For NaraSpeller
        if (p.id === 'naraSpeller') {
          // Check if this is a NaraSpeller configuration
          const isNaraSpeller = config.providerType === 'naraSpeller' || 
                               (!config.providerType && config.serverUrl?.includes('dcplxo2e85.execute-api')) ||
                               (!config.providerType && !config.serverUrl && config.apiKey)
          
          if (isNaraSpeller) {
            return {
              ...p,
              enabled: true,
              apiKey: config.apiKey || '',
              serverUrl: p.defaultServerUrl || config.serverUrl || ''
            }
          }
        }
        
        // For Bareun
        if (p.id === 'bareun') {
          // Check if this is a Bareun configuration
          const isBareun = config.providerType === 'bareun' ||
                          (!config.providerType && config.serverUrl && !config.serverUrl.includes('dcplxo2e85.execute-api'))
          
          if (isBareun && config.serverUrl) {
            return {
              ...p,
              enabled: true,
              apiKey: config.apiKey || '',
              serverUrl: config.serverUrl || ''
            }
          }
        }
        
        return { ...p, enabled: false }
      })
    }
    
    // Subscribe to subscription updates
    const unsubscribe = subscriptionStore.subscribe($sub => {
      subscription = {
        type: $sub.type,
        dailySpellCheckLimit: $sub.dailySpellCheckLimit,
        currentDailyUsage: $sub.currentDailyUsage,
        monthlyUsage: $sub.currentMonthlyUsage?.spellChecks || $sub.currentDailyUsage.spellChecks * 30
      }
    })
    
    return unsubscribe
  })
  
  function toggleProvider(index: number) {
    const provider = providers[index]
    
    // If disabling, just toggle off
    if (provider.enabled) {
      providers = providers.map((p, i) => ({
        ...p,
        enabled: false
      }))
      debouncedSave()
      return
    }
    
    // If enabling, just expand the form - don't validate yet
    // Validation will happen when fields are filled
    providers = providers.map((p, i) => ({
      ...p,
      enabled: i === index
    }))
    
    // Don't save yet if required fields are missing
    // This allows the form to expand without showing errors
    if (provider.id === 'bareun' && (!provider.serverUrl || !provider.apiKey)) {
      // Just expand the form without saving
      return
    }
    
    if (provider.id === 'naraSpeller' && !provider.apiKey) {
      // Just expand the form without saving
      return
    }
    
    // Only save if all required fields are present
    debouncedSave()
  }
  
  function updateApiKey(index: number, value: string) {
    const provider = providers[index]
    providers[index].apiKey = value
    
    // Check if we should auto-enable or auto-disable
    if (provider.enabled) {
      // If already enabled, check if we should disable
      if (provider.id === 'bareun' && (!value || !provider.serverUrl)) {
        // Keep enabled but don't save to settings
        return
      }
      if (provider.id === 'naraSpeller' && !value) {
        // Keep enabled but don't save to settings
        return
      }
    } else {
      // If not enabled, check if we can auto-enable
      if (provider.id === 'naraSpeller' && value) {
        // Auto-enable when API key is entered
        providers[index].enabled = true
        // Disable other providers
        providers = providers.map((p, i) => ({
          ...p,
          enabled: i === index
        }))
      } else if (provider.id === 'bareun' && value && provider.serverUrl) {
        // Auto-enable when both fields are filled
        providers[index].enabled = true
        // Disable other providers
        providers = providers.map((p, i) => ({
          ...p,
          enabled: i === index
        }))
      }
    }
    
    // Save if all required fields are present
    if (provider.enabled) {
      if (provider.id === 'bareun' && provider.serverUrl && value) {
        debouncedSave()
      } else if (provider.id === 'naraSpeller' && value) {
        debouncedSave()
      }
    }
  }
  
  function updateServerUrl(index: number, value: string) {
    const provider = providers[index]
    providers[index].serverUrl = value
    
    // Check if we should auto-enable or keep enabled
    if (provider.enabled) {
      // If already enabled, check if we should keep saving
      if (provider.id === 'bareun' && (!value || !provider.apiKey)) {
        // Keep enabled but don't save to settings
        return
      }
    } else {
      // If not enabled, check if we can auto-enable
      if (provider.id === 'bareun' && value && provider.apiKey) {
        // Auto-enable when both fields are filled
        providers[index].enabled = true
        // Disable other providers
        providers = providers.map((p, i) => ({
          ...p,
          enabled: i === index
        }))
      }
    }
    
    // Save if all required fields are present
    if (provider.enabled && provider.id === 'bareun' && value && provider.apiKey) {
      debouncedSave()
    }
  }
  
  function toggleApiKeyVisibility(providerId: string) {
    showApiKeys[providerId] = !showApiKeys[providerId]
  }
</script>

<div class="api-settings">
  <h2 class="section-title">맞춤법 서버</h2>
  <p class="section-description">
    한국어 맞춤법 검사를 위한 서버를 설정합니다.
  </p>
  
  <!-- Usage Display Component -->
  <UsageDisplay usage={subscription} />
  
  <!-- Grammar Provider Cards -->
  <div class="providers-container">
    {#each providers as provider, index}
      <GrammarProviderCard
        {provider}
        showApiKey={showApiKeys[provider.id]}
        onToggle={() => toggleProvider(index)}
        onUpdateApiKey={(value) => updateApiKey(index, value)}
        onUpdateServerUrl={(value) => updateServerUrl(index, value)}
        onToggleApiKeyVisibility={() => toggleApiKeyVisibility(provider.id)}
      />
    {/each}
  </div>
  
  <!-- Auto-save indicator -->
  {#if showSaveIndicator}
    <div class="save-indicator">
      <span class="save-icon">✓</span>
      <span>저장됨</span>
    </div>
  {/if}
</div>

<style>
  .api-settings {
    padding: 24px;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  
  .section-description {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0 0 24px 0;
  }
  
  .providers-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 20px;
  }
  
  /* Save Indicator */
  .save-indicator {
    position: fixed;
    bottom: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: var(--success);
    color: white;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    animation: slideIn 0.3s ease;
    z-index: 1000;
  }
  
  @keyframes slideIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
</style>